import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Botao, Campo, Cartao, ErroApi, Mensagem } from '../../components/ui'
import { api, ApiError } from '../../lib/api'
import { useAuth } from './store'

/**
 * Cadastro do segundo fator (TOTP).
 *
 * Serve dois casos:
 *  - usuario autenticado que decide ativar o MFA (rota /minha-conta);
 *  - papel privilegiado (ADMIN_MASTER, OPERATOR) barrado no login por nao ter
 *    MFA — o servidor devolve 403 com mfaEnrollmentRequired (SPEC 7.2).
 *
 * O segundo caso tem uma restricao real: sem login concluido nao ha access
 * token, e /auth/mfa/enroll exige autenticacao. Por isso o fluxo obrigatorio
 * comeca por definir a senha nova (mustChangePassword) numa sessao valida; a
 * tela abaixo explica o caminho em vez de travar em silencio.
 */

export function MfaObrigatorio() {
  return (
    <div className="pagina-centrada">
      <Cartao
        titulo="Segundo fator obrigatório"
        descricao="Sua conta tem papel privilegiado na plataforma."
        largo
      >
        <Mensagem tipo="warning" titulo="Verificação em dois fatores necessária">
          Contas com papel <strong>Administrador Master</strong> ou <strong>Operador</strong> só
          entram com autenticação em dois fatores. É a superfície mais sensível do sistema: uma
          senha vazada não pode bastar.
        </Mensagem>

        <ol
          className="ifr-lista-passos"
        >
          <li>
            Instale um aplicativo autenticador — Google Authenticator, Microsoft Authenticator,
            Aegis ou 1Password.
          </li>
          <li>
            Peça a um administrador que já tenha acesso para ativar o segundo fator da sua conta,
            ou use o utilitário de linha de comando no servidor.
          </li>
          <li>Volte ao login e informe o código de 6 dígitos junto com a senha.</li>
        </ol>

        <div
          className="ifr-caixa-aviso ifr-mt-g"
        >
          <strong className="ifr-forte-cor">Primeiro acesso da plataforma?</strong>
          <p className="ifr-mt-p ifr-sem-margem">
            As contas de administrador master são criadas pelo seed com senha aleatória, impressa
            uma única vez no log do deploy. No servidor:
          </p>
          <code
            className="ifr-codigo-bloco"
          >
            sudo -u ifretes node /opt/ifretes/apps/api/dist/cli.js mfa:ativar seu@email.com
          </code>
        </div>

        <div className="ifr-mt-g">
          <Link to="/entrar" className="br-button secondary block">
            Voltar ao login
          </Link>
        </div>
      </Cartao>
    </div>
  )
}

/** Ativacao do MFA por um usuario ja autenticado. */
export function AtivarMfa() {
  const navegar = useNavigate()
  const { usuario, definirUsuario } = useAuth()

  const [etapa, setEtapa] = useState<'inicio' | 'codigo' | 'pronto'>('inicio')
  const [segredo, setSegredo] = useState('')
  const [otpauth, setOtpauth] = useState('')
  const [totp, setTotp] = useState('')
  const [codigosRecuperacao, setCodigos] = useState<string[]>([])
  const [ocupado, setOcupado] = useState(false)
  const [erro, setErro] = useState<unknown>(null)

  async function iniciar() {
    setErro(null)
    setOcupado(true)
    try {
      const r = await api.mfaIniciar()
      setSegredo(r.secret)
      setOtpauth(r.otpauth)
      setEtapa('codigo')
    } catch (err) {
      setErro(err)
    } finally {
      setOcupado(false)
    }
  }

  async function confirmar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setOcupado(true)
    try {
      const r = await api.mfaConfirmar(totp)
      setCodigos(r.recoveryCodes)
      setEtapa('pronto')
      // Reflete no estado local que a conta agora tem MFA, sem novo login.
      if (usuario) definirUsuario({ ...usuario, mfaEnabled: true })
    } catch (err) {
      setErro(err)
    } finally {
      setOcupado(false)
    }
  }

  return (
    <div className="ifr-pagina ifr-pagina--forma">
      <h1 className="ifr-titulo">Verificação em dois fatores</h1>
      <p className="ifr-apoio">
        Um código que muda a cada 30 segundos, além da senha. Protege a conta mesmo se a senha
        vazar.
      </p>

      <ErroApi erro={erro} />

      {etapa === 'inicio' && (
        <div className="ifr-pilha">
          {usuario?.mfaEnabled ? (
            <Mensagem tipo="success" titulo="Já está ativo">
              Esta conta já usa verificação em dois fatores.
            </Mensagem>
          ) : (
            <>
              <Mensagem tipo="info" titulo="Antes de começar">
                Tenha um aplicativo autenticador instalado no celular: Google Authenticator,
                Microsoft Authenticator, Aegis ou 1Password.
              </Mensagem>
              <Botao aoClicar={iniciar} carregando={ocupado}>
                Começar
              </Botao>
            </>
          )}
        </div>
      )}

      {etapa === 'codigo' && (
        <div className="ifr-pilha">
          <Mensagem tipo="info" titulo="Cadastre a chave no aplicativo">
            Toque em "adicionar conta" → "inserir chave manualmente" e cole a chave abaixo.
          </Mensagem>

          <div>
            <label
              className="ifr-rotulo-campo ifr-fraco"
            >
              Chave secreta
            </label>
            <code
              className="ifr-segredo"
            >
              {segredo}
            </code>
            <p className="ifr-mini ifr-mais-fraco ifr-mt-p">
              Conta: <strong>{usuario?.email}</strong> · Emissor: <strong>iFretes</strong> ·{' '}
              <a href={otpauth} className="ifr-marca-cor">
                abrir no aplicativo
              </a>
            </p>
          </div>

          <form onSubmit={confirmar} className="ifr-pilha">
            <Campo
              id="totp"
              rotulo="Código gerado pelo aplicativo"
              valor={totp}
              aoMudar={(v) => setTotp(v.replace(/\D/g, '').slice(0, 6))}
              obrigatorio
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              dica="6 dígitos. Se der erro, aguarde o código trocar e tente o novo."
              autoFocus
            />
            <Botao tipo="submit" carregando={ocupado} desabilitado={totp.length !== 6}>
              Ativar segundo fator
            </Botao>
          </form>
        </div>
      )}

      {etapa === 'pronto' && (
        <div className="ifr-pilha">
          <Mensagem tipo="success" titulo="Segundo fator ativado">
            A partir do próximo login, o código será solicitado junto com a senha.
          </Mensagem>

          <Mensagem tipo="warning" titulo="Guarde os códigos de recuperação agora">
            Estes códigos aparecem <strong>uma única vez</strong>. Cada um serve para entrar uma vez
            se você perder o celular. Sem eles e sem o aplicativo, só um administrador consegue
            recuperar a conta.
          </Mensagem>

          <div
            className="ifr-codigos-recuperacao"
          >
            {codigosRecuperacao.map((c) => (
              <code
                key={c}
                className="ifr-codigo-recuperacao"
              >
                {c}
              </code>
            ))}
          </div>

          <div className="ifr-linha">
            <Botao
              variante="secondary"
              aoClicar={() => {
                void navigator.clipboard?.writeText(codigosRecuperacao.join('\n'))
              }}
            >
              Copiar códigos
            </Botao>
            <Botao aoClicar={() => navegar('/minha-conta')}>Concluir</Botao>
          </div>
        </div>
      )}
    </div>
  )
}
